import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { useCallback, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { Period } from 'dashboard/lib/unixTime';
import { getEffectivePeriod } from 'dashboard/lib/utils/period';
import { ProductFeatureName } from 'dashboard/models/organization';
import { showMessage } from 'dashboard/redux/actions/messages';
import { resetMetricsDateFilter, setMetricsFilters } from 'dashboard/redux/actions/metrics-inquiry';
import { getAdjustedFilters } from 'dashboard/redux/selectors/metrics-inquiry';
import { RootState } from 'dashboard/redux/store';
import {
  loadMetricsTemplateFunnelConversionOverTime,
  loadMetricsTemplateFunnelFlow,
  setGranularities,
} from 'dashboard/redux/thunk-actions/metrics-inquiry';
import InquiryFunnelAnalyticsDetailedView from 'dashboard/views/InquiryFunnelAnalyticsDetailedView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useTable from 'lib/hooks/useTable';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { translateDateValuetoLuxon } from 'lib/utils/filters';

type Params = {
  inquiryTemplateId: string;
  inquiryTemplateVersionId: string;
  stepIds: string;
};

const DashboardInquiryFunnelDetailedViewRoute = (props: PropsFromRedux) => {
  const {
    organization,
    currentEnvironment,
    inquiryTemplates,
    templateFunnelFlows,
    templateFunnelConversionOverTime,
    filters,
    granularities,
    lastQueryFilters,
  } = props;

  const params = useParams<Params>() as Params; // Will always be defined

  const { handleDateUpdate } = useTable({
    filters,
    setFilters: props.setMetricsFilters,
  });

  const handleDateUpdateOrClearV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      if (newDateRange != null) {
        const { startDate, endDate } = translateDateValuetoLuxon(newDateRange);
        startDate || endDate
          ? handleDateUpdate({ startDate, endDate })
          : props.resetMetricsDateFilter();
      }
    },
    [handleDateUpdate, props.resetMetricsDateFilter],
  );

  const productFeatureIsDisabled = productFeatureDisabled(
    organization,
    ProductFeatureName.InquiriesAnalytics,
    currentEnvironment.type,
  );
  const inquiryTemplateId = params.inquiryTemplateId;
  const inquiryTemplateVersionId = params.inquiryTemplateVersionId;
  const stepIds = params.stepIds?.split(',') ?? [];

  const {
    loading: funnelFlowMetricsLoading,
    error: funnelFlowMetricsError,
    callback: loadFunnelFlowMetrics,
  } = useApiCallback(
    async ({
      inquiryTemplateId,
      inquiryTemplateVersionId,
    }: {
      inquiryTemplateId: string;
      inquiryTemplateVersionId: string;
    }) => {
      const effectivePeriod = getEffectivePeriod(granularities.funnel, [
        filters.createdAtStart,
        filters.createdAtEnd,
      ]);
      const funnelFlow = props.loadMetricsTemplateFunnelFlow({
        currentEnvironment,
        filters: {
          ...filters,
          templateOrInquiryTemplateId: inquiryTemplateId,
        },
      });
      const funnelConversionOverTime = props.loadMetricsTemplateFunnelConversionOverTime({
        currentEnvironment,
        filters: {
          ...filters,
          templateOrInquiryTemplateId: inquiryTemplateId,
          inquiryTemplateVersionId: inquiryTemplateVersionId,
          funnelStepIds: stepIds,
        },
        funnelPeriod: effectivePeriod,
      });
      await Promise.all([
        props.setGranularities({
          granularities: {
            ...granularities,
            funnel: effectivePeriod,
          },
        }),
        funnelFlow,
        funnelConversionOverTime,
      ]);
    },
    [currentEnvironment, filters, stepIds, inquiryTemplateId],
  );

  const {
    loading: loadingGranular,
    error: errorGranular,
    callback: handleUpdateGranularity,
  } = useApiCallback(
    async (period: Period) => {
      await Promise.all([
        props.setGranularities({
          granularities: {
            ...granularities,
            funnel: getEffectivePeriod(period, [filters.createdAtStart, filters.createdAtEnd]),
          },
        }),
        props.loadMetricsTemplateFunnelConversionOverTime({
          currentEnvironment,
          filters: {
            ...filters,
            templateOrInquiryTemplateId: inquiryTemplateId,
            inquiryTemplateVersionId: inquiryTemplateVersionId,
            funnelStepIds: stepIds,
          },
          funnelPeriod: period,
        }),
      ]);
    },
    [currentEnvironment, filters, stepIds, inquiryTemplateId, granularities],
  );

  useApiErrorHandler({
    error: errorGranular,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load template funnel metrics.');
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  useApiErrorHandler({
    error: funnelFlowMetricsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load template funnel metrics.');
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });
  useEffect(() => {
    loadFunnelFlowMetrics({ inquiryTemplateId, inquiryTemplateVersionId });
  }, []);

  const inquiryTemplate = inquiryTemplates.find((item) => item.id === inquiryTemplateId);
  return (
    <InquiryFunnelAnalyticsDetailedView
      productFeatureIsDisabled={productFeatureIsDisabled}
      loading={funnelFlowMetricsLoading || loadingGranular}
      emptyData={
        inquiryTemplates.length === 0 ||
        templateFunnelFlows.length === 0 ||
        templateFunnelConversionOverTime.data.length === 0
      }
      template={inquiryTemplate}
      filters={filters}
      lastQueryFilters={lastQueryFilters.funnel}
      funnelPeriod={granularities.funnel}
      handleDateUpdateV2={handleDateUpdateOrClearV2}
      handleUpdateGranularity={handleUpdateGranularity}
      loadMetrics={loadFunnelFlowMetrics}
      stepIds={stepIds}
      templateFunnelFlow={templateFunnelFlows.find(
        (templateFunnelFlow) => templateFunnelFlow.token === inquiryTemplateVersionId,
      )}
      templateFunnelConversionOverTime={templateFunnelConversionOverTime}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  organization: state.organization.organization,
  currentEnvironment: state.currentEnvironment.currentEnvironment,
  inquiryTemplates: state.organization.inquiryTemplates,
  filters: getAdjustedFilters(state),
  lastQueryFilters: state.metricsInquiry.lastQueryFilters,
  templateFunnelConversionOverTime: state.metricsInquiry.templateFunnelConversionOverTime,
  templateFunnelFlows: state.metricsInquiry.templateFunnelFlows,
  granularities: state.metricsInquiry.granularities,
});

const mapDispatchToProps = {
  loadMetricsTemplateFunnelFlow,
  loadMetricsTemplateFunnelConversionOverTime,
  setMetricsFilters,
  setGranularities,
  resetMetricsDateFilter,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardInquiryFunnelDetailedViewRoute);
